import React, { useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';

import {
  ARTICLE_TYPES,
  BUTTON_TYPES,
  ICON_TYPES,
} from '../../../shared/constants';
import {
  ArticleQueryItem,
  IArticle,
  IImage,
  ProductQueryItem,
  Tag,
} from '../../../types/shared/shared.types';

import ArticlePreviewsWrapper from '../../atoms/article-previews-wrapper/ArticlePreviewsWrapper';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { BUTTON_LABELS, languageHelper } from '../../../utils/language';
import { ARTICLE_RESULTS } from '../../../utils/language/wizard-labels';

import mq from '../../../styles/breakpoints';
import { Body, H2 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { TagTypes } from '../../../tokens';
import ArticlePreviewCard from '../../molecules/article-preview-card/ArticlePreviewCard';
import WizardResultCard from '../../molecules/wizard-result-card/WizardResultCard';

import {
  getFilteredArticlesURL,
  parseQueryParameterToTags,
  parseSectionsData,
} from './WizardResultOverview.helpers';
import {
  ResultSectionProps,
  WizardResultOverviewProps,
} from './WizardResultOverview.types';

const WizardResultOverview = ({
  tags,
  queryTags,
}: WizardResultOverviewProps) => {
  const wizardResultOverviewQuery = useStaticQuery(graphql`
    query productPagesQuery {
      allContentfulPageGeneric(
        filter: {
          pageType: { in: ["product-page", "gated-contact-page"] }
          isDisabled: { ne: true }
        }
        sort: { updatedAt: DESC }
      ) {
        nodes {
          slug
          ...ParentPageGeneric
          tags {
            id
            name
            value
            type
          }
          pageName
          overviewCard {
            ...Card
          }
        }
      }
      allContentfulPageArticle(
        filter: { isDisabled: { ne: true } }
        sort: { publishingTime: DESC }
      ) {
        nodes {
          id
          title
          subHeader
          lead {
            lead
          }
          slug
          ...ParentPageArticle
          tags {
            id
            name
            value
            type
          }
          mainImage {
            gatsbyImageData(layout: FULL_WIDTH)
          }
          publishingTime(formatString: "YYYY-MM-DD")
          authorRef {
            name
          }
        }
      }
      allContentfulTag {
        nodes {
          id
          name
          value
          type
        }
      }
    }
  `);

  const allProductPagesData: ProductQueryItem[] = useMemo(
    () => wizardResultOverviewQuery.allContentfulPageGeneric?.nodes || [],
    []
  );
  const allArticlePagesData: ArticleQueryItem[] = useMemo(
    () => wizardResultOverviewQuery.allContentfulPageArticle?.nodes || [],
    []
  );
  const allTagsData: Tag[] = useMemo(
    () => wizardResultOverviewQuery.allContentfulTag?.nodes || [],
    []
  );

  const queryResultTags = useMemo(
    () => parseQueryParameterToTags(queryTags, allTagsData),
    [queryTags, allTagsData]
  );

  const tagsToParse = queryResultTags.length ? queryResultTags : tags;
  const sectionsData = parseSectionsData({
    tags: tagsToParse,
    products: allProductPagesData,
    articles: allArticlePagesData,
  });

  return (
    <div>
      {sectionsData.map((item, index) => (
        <ResultSection item={item} key={index} />
      ))}
    </div>
  );
};

const ResultSection = ({ item }: ResultSectionProps) => {
  const { lang } = languageHelper();
  const filteredArticlesURL = getFilteredArticlesURL(item.tag);

  return (
    <TagSection>
      <StyledH2>{item.tag.name}</StyledH2>
      <ContentArticle>
        <List>
          {item.products.map((product, id) => (
            <ListItem key={id}>
              <WizardResultCard
                title={product.title}
                body={product.body}
                primaryLink={product.primaryLink}
                image={product.image as IImage}
                cardNumber={id + 1}
              />
            </ListItem>
          ))}
        </List>
      </ContentArticle>
      <ContentArticle>
        <LinkWrapper>
          <Body>
            {item.tag.type === TagTypes.SECTOR
              ? ARTICLE_RESULTS.title[lang]
              : ''}
          </Body>
          <SecondaryButton
            to={filteredArticlesURL}
            label={BUTTON_LABELS.viewArticles[lang]}
            type={BUTTON_TYPES.internal}
            icon={ICON_TYPES.chevron}
          />
        </LinkWrapper>
        <StyledArticlePreviewsWrapper>
          {item.articles.map((article, id) => (
            <ArticlePreviewCard
              image={article.image?.gatsbyImageData}
              tags={article.tags}
              title={article.title}
              data={article as IArticle}
              articleType={ARTICLE_TYPES.internal}
              cardType={article.previewCardType}
              key={id}
            />
          ))}
        </StyledArticlePreviewsWrapper>
      </ContentArticle>
    </TagSection>
  );
};

export default WizardResultOverview;

const StyledArticlePreviewsWrapper = styled(ArticlePreviewsWrapper)`
  margin-top: 0;

  ${mq.from.breakpoint.M`
    grid-column-gap: ${Spacings.S};
  `}
`;

const StyledH2 = styled(H2)`
  margin: 0;
`;

const TagSection = styled.section`
  margin-top: ${Spacings.XL};

  ${mq.from.breakpoint.M`
    margin-top: ${Spacings.XXL};
  `}
`;

const ContentArticle = styled.article`
  margin-top: ${Spacings.S};

  ${mq.from.breakpoint.M`
    margin-top: ${Spacings.M};
  `};
`;

const LinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacings.S};
  width: 100%;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ListItem = styled.li`
  margin: 0;
  padding: 0;
`;
